import { ExclamationCircleFilled } from "@ant-design/icons";
import { Modal, Spin } from "antd";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Footer from "../components/Footer";
import NavBar from "../components/NavBar";
import SideBar from "../components/SideBar";
import { LOGGED_USER } from "../variables/constants";
import CompanySelectorModal from "../components/CompanySelectorModal";
import axiosInstance from "../variables/api/axiosInstance";
import { toast } from "react-toastify";
import { mockData } from "../utils/navbarUtils";
import {
  evaluatePasswordChangeRequirement,
  PASSWORD_CHANGE_REASON,
} from "../utils/passwordChangePolicy";
import ProductionJournaliereCompagnie from "./production/components/ProductionJournaliereCompagnie";
import { useDernierAcces } from "../hooks/UserDernierAcces";

const { confirm } = Modal;

const Home = () => {
  const [visible, setVisible] = useState(false);       // visibilité du sélecteur de compagnies
  const [loading, setLoading] = useState(true);        // état de chargement des compagnies
  const [companies, setCompanies] = useState([]);      // compagnies filtrées pour l'utilisateur
  const navigate = useNavigate();
  const [searchResults, setSearchResults] = useState([]);
  const [searchValue, setSearchValue] = useState("");

  const { dernierAcces, loadingReprise, reprendre } = useDernierAcces();

  // Évite l’ouverture multiple du confirm en React.StrictMode (effets déclenchés 2x en dev)
  const askedPasswordChangeRef = useRef(false);

  useEffect(() => {
    // 1) Forcer le changement de mot de passe selon une règle unique (backend-driven)
    const decision = evaluatePasswordChangeRequirement(LOGGED_USER);
    if (decision.shouldForce && !askedPasswordChangeRef.current) {
      askedPasswordChangeRef.current = true;
      confirmPasswordChange(decision.reason);
    }

    // 2) Si l'utilisateur est COURTIER, ouvrir le sélecteur de compagnie + charger la liste
    if (LOGGED_USER?.agence?.typeAgence === "COURTIER") {
      setVisible(true);
      const codes =
        LOGGED_USER?.agence?.validAgrements?.map((ag) => ag?.compagnie?.code) ?? [];

      axiosInstance
        .get(`/api/organisme/all/assurances`)
        .then((res) => {
          const data = Array.isArray(res?.data) ? res.data : [];
          setCompanies(data.filter((comp) => codes.includes(comp?.code)));
          setLoading(false);
        })
        .catch((err) => {
          console.error("Erreur lors du chargement des compagnies:", err);
          setCompanies([]);
          setLoading(false);
        });
    } else {
      // Pas COURTIER → rien à charger/afficher
      setLoading(false);
      setVisible(false);
    }
    // tableau de dépendances vide: on évalue à l'arrivée sur la page
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCompanySelect = (company) => {
    toast(`Compagnie ${company?.raisonSociale} sélectionnée avec succès!`, {
      position: "top-right",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      theme: "light",
    });
    // Exemple: fixer le contexte en sessionStorage
    sessionStorage.setItem("currentCompagnie", JSON.stringify(company));
    setVisible(false);
  };

  // Boîte de dialogue non annulable pour forcer le changement du mot de passe
  const confirmPasswordChange = (reason) => {
    const content =
      reason === PASSWORD_CHANGE_REASON.EXPIRED
        ? "Votre mot de passe est expiré. Vous devez en définir un nouveau maintenant."
        : "Pour des raisons de sécurité, vous devez définir un nouveau mot de passe maintenant.";

    confirm({
      title: "Modification du mot de passe requise",
      icon: <ExclamationCircleFilled />,
      content,
      okText: "Changer mon mot de passe",
      okType: "primary",
      okCancel: false,        // supprime le bouton « Annuler / Plus tard »
      maskClosable: false,    // empêche la fermeture par clic sur le masque
      keyboard: false,        // empêche la fermeture par touche Échap
      centered: true,
      onOk() {
        navigate("/first-connexion");
      },
      // pas de onCancel: il n'y a plus de bouton Annuler
    });
  };

  const renderSearchResults = (results) => {
    return (
      results.map(({ category, items }) => (
        <div key={category} className="px-3 py-2">
          <h6 className="text-muted text-uppercase mb-2">
            {category}
          </h6>
          {items
            .filter((item) =>
              item.name.toLowerCase().includes(searchValue.toLowerCase())
            )
            .map((item, index) => (
              <a
                key={index}
                href={item.url}
                className="dropdown-item d-flex align-items-center gap-2 py-1"
              >
                <i className={`bx ${item.icon} text-muted`}></i>
                <span className="text-truncate">{item.name}</span>
              </a>
            ))}
        </div>
      ))
    );
  }

  useEffect(() => {
    if (searchValue && searchValue?.length > 2) {
      let results = [];
      Object.keys(mockData).forEach((key) => {
        const filtered = mockData[key].filter((item) =>
          item.name.toLowerCase().includes(searchValue.toLowerCase())
        );
        if (filtered.length) {
          results.push({ category: key, items: filtered });
        }
      });
      setSearchResults(results);
    }
  }, [searchValue]);

  return (
    <div className="layout-wrapper layout-content-navbar">
      <div className="layout-container">
        <SideBar currentMenu={"DASHBOARD"} />
        <div className="layout-page">
          <NavBar
            filteredResults={searchResults}
            setFilteredResults={setSearchResults}
            renderSearchResults={renderSearchResults}
            searchValue={searchValue}
            setSearchValue={setSearchValue}
          />
          <div className="content-wrapper">
            <div className="container-xxl flex-grow-1 container-p-y">
              <div className="row">
                <div className="col-xxl-8 mb-6 order-0">
                  <div className="card">
                    <div className="d-flex align-items-start row">
                      <div className="col-sm-7">
                        <div className="card-body">
                          <h5 className="card-title text-primary mb-3">
                            Bienvenue {LOGGED_USER?.personne?.prenom}{" "}
                            {LOGGED_USER?.personne?.nom}! 🎉
                          </h5>
                          <p className="mb-6">
                            PoolIns facilite toutes vos opérations et vous rend
                            plus productif.
                            <br />
                            Reprenez où vous vous étiez arrêté à la dernière
                            connexion si vous le souhaitez.
                          </p>
                          <button
                            className="btn btn-sm btn-outline-primary"
                            onClick={reprendre}
                            disabled={loading || !dernierAcces}
                            title={dernierAcces?.libelle ?? 'Aucun dossier récent'}
                          >
                            {/* Reprendre le dernier dossier */}
                            {loading
                              ? 'Chargement...'
                              : dernierAcces
                                ? `Reprendre le dernier dossier`
                                : 'Aucun dossier récent'}
                          </button>
                        </div>
                      </div>
                      <div className="col-sm-5 text-center text-sm-left">
                        <div className="card-body pb-0 px-0 px-md-6">
                          <img
                            src="../assets/img/illustrations/man-with-laptop.png"
                            height="175"
                            className="scaleX-n1-rtl"
                            alt="Illustration utilisateur"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-12">
                  {/* <ProductionJournaliereCompagnie /> */}
                </div>
              </div>
            </div>
            {/* Vous pouvez ajouter d'autres cartes/sections ici */}
            {/* Footer */}
            <Footer />
            {/* /Footer */}
            <div className="content-backdrop fade"></div>
          </div>
          {/* /Content wrapper */}
        </div>
        {/* /Layout page */}
      </div>

      {/* Sélecteur de compagnie (avec écran de chargement) */}
      {loading ? (
        <Modal
          title="Chargement..."
          open={visible}
          closable={false}
          footer={null}
          centered
        >
          <Spin size="large" />
        </Modal>
      ) : (
        <CompanySelectorModal
          visible={visible}
          companies={companies}
          onSelect={handleCompanySelect}
        />
      )}

      {/* Overlay */}
      <div className="layout-overlay layout-menu-toggle show"></div>
    </div>
  );
};

export default Home;
